import { DeepPartial } from "@tellescope/utils";

export interface UpdateOptions {
  replaceObjectFields?: boolean;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  (value.constructor === Object || value.constructor === undefined);

const mergeValue = (existing: unknown, update: unknown): unknown => {
  if (Array.isArray(update)) {
    if (Array.isArray(existing)) {
      const set = new Set(existing);
      update.forEach((item) => set.add(item));
      return Array.from(set);
    }
    return update;
  }

  if (isPlainObject(update)) {
    const base = isPlainObject(existing) ? { ...existing } : {};
    for (const key of Object.keys(update)) {
      base[key] = mergeValue(base[key], update[key]);
    }
    return base;
  }

  return update;
};

export const updateModelWithOptions = <T extends object, O extends UpdateOptions>(
  model: T,
  updates: DeepPartial<T>,
  options?: O
): T => {
  const replaceObjectFields = options?.replaceObjectFields ?? false;
  if (replaceObjectFields !== false) {
    return { ...model, ...(updates as Partial<T>) };
  }

  const result = { ...model };
  for (const key of Object.keys(updates) as Array<keyof T>) {
    const updateValue = (updates as Record<string, unknown>)[key as string];
    result[key] = mergeValue(result[key], updateValue) as T[keyof T];
  }

  return result;
};
